import { type JobProgressionContent } from '@/features/messaging/content'
import { type Message } from '@/features/messaging/Message'
import { type MessageContentViewModel } from '@/features/messaging/MessageViewModel'
import type { JobProgressionFn } from '@/features/jobs'
import type { JobProgression } from '@/features/jobs/taxes/progression/JobProgression'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction } from '@/utils/mobx-util'
import { computed, observable } from 'mobx'

/**
 * Content of a message that reports a job progression step.
 */
export class JobProgressionMessageContentViewModel
  implements MessageContentViewModel, ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  @observable.ref private accessor _progression: JobProgression | undefined =
    undefined

  private readonly _dateFormatter = new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'medium',
  })

  constructor(
    private readonly _canUpdateJobProgressionAsClient: boolean,
    public readonly message: Message,
    public readonly onViewRejectDraft: () => void,
    public readonly onViewApproveDraft: () => void,
    private readonly _jobProgression: JobProgressionFn,
  ) {
    if (this._content.jobId) {
      this._lifecycle.manage(
        this._jobProgression(this._content.jobId).pipe(
          tapAction((progression) => {
            this._progression = progression
          }),
        ),
      )
    }
  }

  private get _content() {
    return this.message.content as JobProgressionContent
  }

  /**
   * Whether the client can approve or reject the draft from the message.
   */
  @computed
  get canAnswerDraftInReviewMessage() {
    return (
      this.progressionStep?.status === 'DraftInReview' &&
      this._canUpdateJobProgressionAsClient
    )
  }

  /**
   * The progression step the message refers to, once loaded.
   */
  @computed
  get progressionStep() {
    return this._progression?.steps.find((s) => s.id === this._content.stepId)
  }

  /**
   * Whether the step already has a response.
   */
  @computed
  get hasResponse() {
    return Boolean(this.progressionStep?.response)
  }

  /**
   * The message title.
   */
  @computed
  get title() {
    switch (this.progressionStep?.status) {
      case 'DraftApproved':
        return 'The return has been approved.'
      case 'DraftRejected':
        return 'Changes have been requested'
      case 'DraftInReview':
        return 'The return is now ready for review'
      default:
        return ''
    }
  }

  /**
   * The message description.
   */
  @computed
  get description() {
    switch (this.progressionStep?.status) {
      case 'DraftApproved':
      case 'DraftRejected':
        return this._dateFormatter.format(this.progressionStep.dateCreated)
      case 'DraftInReview':
        return 'The return is now ready for review'
      default:
        return ''
    }
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
